import { Injectable, signal, WritableSignal } from '@angular/core';
import { CookieService } from 'ngx-cookie-service'; // Adjust the import path if necessary
import { UserInfo } from '../model/user-info';
import { environment } from '../../../../environments/environment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ClientDeviceInfo } from '../../../model/client-device-info';
import { SharedUtils } from '../utilities/shared-utils';
import { LegalEntityInfo } from '../model/legal-entity-info';
import { EnumLegalEntityRole, LegalEntityBasicInfo } from '../model/legal-entity-basic-info';
import { CitizenInfo } from '../model/citizen-info';
import { EnumRoleFullIdentifier, EnumUserRole } from '../model/user-basic-info';
import { UserLegalEntityBasicDetail } from '../model/user-legal-entity-basic-detail';
import { EnumUserLegalEntityRole, UserLegalEntityInfo } from '../model/user-legal-entity-info';

@Injectable({
  providedIn: 'root'
})
export class BuildingsCookieService {

    private clientUserInfo: WritableSignal<UserInfo> = signal<UserInfo>(new UserInfo());;
    private clientDeviceInfo: WritableSignal<ClientDeviceInfo> = signal<ClientDeviceInfo>(new ClientDeviceInfo());

    constructor(private cookieService: CookieService, private deviceDetectorService: DeviceDetectorService) {
        this.clientDeviceInfo().fromDeviceInfo(this.deviceDetectorService.getDeviceInfo(), 
        this.deviceDetectorService.isDesktop(), this.deviceDetectorService.isTablet(), this.deviceDetectorService.isMobile());
    }

     /********************************************************
     *  Public methods
     *******************************************************/

    private instantiateClientUserInfo(): boolean {
        if ( SharedUtils.inputValueIsPresent(this.clientUserInfo()) && (this.clientUserInfo()?.id) && ( (this.clientUserInfo()?.id ?? 0) > 0) && (this.clientUserInfo() instanceof UserInfo) ) return true;
        // else console.log("BuildingsCookieService.instantiateClientUserInfo() - INSTANTIATING USER INFO ...");

        if( this.isInLocalStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(UserInfo.parseUserInfo(userInfo) ?? new UserInfo());
            return true;
        }
        if( this.isInSessionStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? "");
            this.clientUserInfo.set(UserInfo.parseUserInfo(userInfo) ?? new UserInfo());
            return true;
        }
        return false;
     }

    public login(userInfo: UserInfo): boolean {
        // console.log("BuildingsCookieService.login()");
        // console.log("BuildingsCookieService.login(). UserInfo: " + JSON.stringify(userInfo));
        const authenticationType: string = this.cookieService.get(environment.authenticationTypeCookieName) // 'local' or 'session'
        // console.log("BuildingsCookieService.login(). Authentication type: " + authenticationType);
        if(authenticationType) {
            localStorage.setItem('cookiesDisclaimerViewed', 'true');
            localStorage.setItem('cookiesAccepted', 'true');
            this.clientUserInfo.set(UserInfo.parseUserInfo(userInfo) ?? new UserInfo());

            if (this.cookieService.get(environment.authenticationTypeCookieName) === environment.authenticationTypeCookieValue_local) {
                localStorage.setItem('userInfo', JSON.stringify(this.clientUserInfo()));
                localStorage.setItem('userRoles', JSON.stringify(this.clientUserInfo()?.getAuthorities()));
            } else {
                sessionStorage.setItem('userInfo', JSON.stringify(this.clientUserInfo()));
                sessionStorage.setItem('userRoles', JSON.stringify(this.clientUserInfo()?.getAuthorities()));
            }
            //this.clientUserInfo = Object.assign(new UserInfo(), userInfo);
            return true;
        }
        this.logout();
        return false;
    }

    public updateClientUserInfo(newUserInfo: UserInfo): boolean {
        // console.log("BuildingsCookieService.updateClientUserInfo(). New UserInfo: ", JSON.stringify(newUserInfo));
       return this.login(newUserInfo);
    }

    public checkPermissions(rolesFullIdentifier: EnumRoleFullIdentifier[]): boolean {
        // console.log("BuildingsCookieService.checkPermissions - rolesFullIdentifier: " + rolesFullIdentifier);
        if (this.instantiateClientUserInfo() && rolesFullIdentifier && rolesFullIdentifier.length > 0) {
            for (const roleFullIdentifier of rolesFullIdentifier) {
                let result: boolean = this.checkPermission(roleFullIdentifier);
                if (result) {
                    return true;
                }
            }
        }
        return false;
    }

    private checkPermission(roleFullIdentifier: EnumRoleFullIdentifier): boolean {
        const inputUserRole: EnumUserRole = ( roleFullIdentifier.toString().split(".")[0] as EnumUserRole );
        const inputLegalEntityRole: EnumLegalEntityRole = ( roleFullIdentifier.toString().split(".")[1] as EnumLegalEntityRole );
        const inputUserLegalEntityRole: EnumUserLegalEntityRole = ( roleFullIdentifier.toString().split(".")[2] as EnumUserLegalEntityRole );
        // console.log("BuildingsCookieService.checkPermission - roleFullIdentifier: " + inputUserRole + "." + inputLegalEntityRole + "." + inputUserLegalEntityRole);
        let userRoles: EnumRoleFullIdentifier[] = new Array<EnumRoleFullIdentifier>();
        if( this.isInLocalStorage("userRoles") ) {
            userRoles = JSON.parse(localStorage.getItem('userRoles') ?? "");
        }
        if ( this.isInSessionStorage("userRoles") ) {
            userRoles = JSON.parse(sessionStorage.getItem('userRoles') ?? "");
        }
        for (const userRole of userRoles) {
            // console.log("BuildingsCookieService.checkPermission - userRole:", userRole);
            const tempUserRole: EnumUserRole = ( userRole.toString().split(".")[0] as EnumUserRole );
            const tempLegalEntityRole: EnumLegalEntityRole = ( userRole.toString().split(".")[1] as EnumLegalEntityRole );
            const tempUserLegalEntityRole: EnumUserLegalEntityRole = ( userRole.toString().split(".")[2] as EnumUserLegalEntityRole );
            // console.log("BuildingsCookieService.checkPermission - tempUserRole: " + tempUserRole + "." + tempLegalEntityRole + "." + tempUserLegalEntityRole);
            const userRoleMatch: boolean = ( inputUserRole === (SharedUtils.getEnumKey(EnumUserRole, EnumUserRole.ANY) as EnumUserRole) ? true : (inputUserRole === tempUserRole) );
            const legalEntityRoleMatch: boolean = ( inputLegalEntityRole === (SharedUtils.getEnumKey(EnumLegalEntityRole, EnumLegalEntityRole.ANY) as EnumLegalEntityRole) ? true : (inputLegalEntityRole === tempLegalEntityRole) );
            const userLegalEntityRoleMatch: boolean = ( inputUserLegalEntityRole === (SharedUtils.getEnumKey(EnumUserLegalEntityRole, EnumUserLegalEntityRole.ANY) as EnumUserLegalEntityRole) ? true : (inputUserLegalEntityRole === tempUserLegalEntityRole) );
            // console.log("BuildingsCookieService.checkPermission - rolesMatch: " + userRoleMatch + " " + legalEntityRoleMatch + " " + userLegalEntityRoleMatch);
            if(userRoleMatch && legalEntityRoleMatch && userLegalEntityRoleMatch) {
                return true;
            }
        }
        return false;
    }

    public checkAuthentication(): boolean{
        if ( /*(this.cookieService.get(environment.authenticationTypeCookieName) ?? '').length > 0 && */( (this.isInLocalStorage('userInfo') && this.isInLocalStorage('userRoles') &&
          this.isCookiesDisclaimerViewed() && this.isCookiesAccepted() && this.cookieService.check(environment.authenticationTypeCookieName)) ||
          (this.isInSessionStorage('userInfo') && this.isInSessionStorage('userRoles') &&
          this.isCookiesDisclaimerViewed() && this.isCookiesAccepted() && this.cookieService.check(environment.authenticationTypeCookieName))) ) {
            return true;
        } else {
            this.logout();
            return false;
        }
    }

    public isAuthenticated(): boolean {
        // console.log("BuildingsCookieService.isAuthenticated(). cookie type: " + this.cookieService.get(environment.authenticationTypeCookieName));
        if ( /*( this.cookieService.get(environment.authenticationTypeCookieName) ?? '').length > 0 &&*/ ( (this.isInLocalStorage('userInfo') && this.isInLocalStorage('userRoles') &&
        this.cookieService.check(environment.authenticationTypeCookieName)) ||
        (this.isInSessionStorage('userInfo') && this.isInSessionStorage('userRoles') &&
        this.cookieService.check(environment.authenticationTypeCookieName))) ) {
            return true;
        } else {
            return false;
        }
    }

    public logout():void {
        localStorage.removeItem('userInfo');
        localStorage.removeItem('userRoles');
        localStorage.removeItem('userLegalEntityRoles');
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('userRoles');
        sessionStorage.removeItem('userLegalEntityRoles');
        this.cookieService.delete(environment.authenticationTypeCookieName);
        setTimeout( () => {
            this.clientUserInfo.set(new UserInfo());
        }, 100);
        console.log("BuildingsCookieService.logout: user logged out");
    }

    public getClientUserRoles(): Array<EnumRoleFullIdentifier> | undefined {
        if ( this.isInSessionStorage('userInfo') ) {
            return JSON.parse(sessionStorage.getItem('userRoles') ?? "") as Array<EnumRoleFullIdentifier>;
        }
        if ( this.isInLocalStorage('userInfo') ) {
            return JSON.parse(localStorage.getItem('userRoles') ?? "") as Array<EnumRoleFullIdentifier>;
        }
        return undefined;
    }

    public getClientCitizenFullName(): string | undefined {
        if( SharedUtils.inputValueIsPresent(this.clientUserInfo()) && SharedUtils.inputValueIsPresent(this.clientUserInfo()?.citizenInfo) ) {
            return ( this.clientUserInfo()?.citizenInfo?.name + " " + this.clientUserInfo()?.citizenInfo?.surname);
        }
        return undefined;
    }

    public acceptCookies(): void {
        this.setCookiesAccepted(true);
        this.setCookiesDisclaimerViewed(true);
    }

    public checkCookies(): boolean {
        return ( this.isCookiesDisclaimerViewed() && this.isCookiesAccepted() );
    }
/*
    public refuseCookies() {
        localStorage.clear();
        sessionStorage.clear();
        localStorage.setItem('cookiesDisclaimerViewed', 'true');
        localStorage.setItem('cookiesAccepted', 'false');
    }

    public clearDisclaimers(): void {
        localStorage.removeItem('privacyDisclaimerViewed');
        localStorage.removeItem('cookieDisclaimerViewed');
        localStorage.removeItem('cookiesAccepted');
    }

    public checkDisclaimersAccepted(): boolean {
        const privacyDisclaimerViewed: boolean = localStorage.getItem('privacyDisclaimerViewed') === 'true';
        const cookieDisclaimerViewed: boolean = localStorage.getItem('cookieDisclaimerViewed') === 'true';
        const cookiesAccepted: boolean = localStorage.getItem('cookiesAccepted') === 'true';

        return (privacyDisclaimerViewed && cookieDisclaimerViewed && cookiesAccepted);
    }

    public checkDisclaimersViewed(): boolean {
        const cookieDisclaimerViewed: string | null = localStorage.getItem('cookieDisclaimerViewed');
        const privacyDisclaimerViewed: string | null = localStorage.getItem('privacyDisclaimerViewed');
        const cookiesAccepted: string | null = localStorage.getItem('cookiesAccepted');
        return ( !!privacyDisclaimerViewed && !!cookieDisclaimerViewed && !!cookiesAccepted );
    }

    public checkCookiesAccepted(cookiesAccepted: boolean): void {
        localStorage.setItem('cookiesAccepted', cookiesAccepted.toString());
    }
*/

    public encryptData(data: string, encryptSalt: string): Promise<string> {
        return SharedUtils.symmetricEncrypt(data, this.getClientDeviceFingerprint(), encryptSalt).then( (encryptedData: string) => {
            return encryptedData;
        });
    }

    /********************************************************
     *  Private methods
     *******************************************************/
/*
    private isPrivacyDisclaimerViewed(privacyDisclaimerViewed: boolean): void {
        localStorage.setItem('privacyDisclaimerViewed', privacyDisclaimerViewed.toString());
    }

    private isCookieDisclaimerViewed(cookieDisclaimerViewed: boolean): void {
        localStorage.setItem('cookieDisclaimerViewed', cookieDisclaimerViewed.toString());
    }

    private disclaimerViewed(): boolean {
        return ( this.isInLocalStorage('cookiesDisclaimerViewed') && this.isInLocalStorage('cookiesAccepted') );
    }

    private cookiesAccepted(): boolean {
        return localStorage.getItem("cookiesAccepted")?.toLowerCase() === 'true';
    }

    private clearDisclaimerViewed(): void {
        localStorage.removeItem("cookiesAccepted");
        localStorage.removeItem('cookiesDisclaimerViewed');
    }
*/

    private isCookiesDisclaimerViewed(): boolean {
        return localStorage.getItem('cookiesDisclaimerViewed')?.toLowerCase() === 'true';
    }

    private isCookiesAccepted(): boolean {
        return localStorage.getItem("cookiesAccepted")?.toLowerCase() === 'true';
    }

    private isInSessionStorage(key: string): boolean {
        return sessionStorage.getItem(key) !== null;
    }

    private isInLocalStorage(key: string): boolean {
        return localStorage.getItem(key) !== null;
    }

    private setCookiesAccepted(accepted: boolean): void {
        localStorage.setItem("cookiesAccepted", accepted.toString());
    }

    private setCookiesDisclaimerViewed(viewed: boolean): void {
        localStorage.setItem("cookiesDisclaimerViewed", viewed.toString());
    }

    /********************************************************
    *********************************************************
    *
    *              CLIENT DEVICE INFORMATIONS
    * 
    ********************************************************
    *******************************************************/

    /*******************************************************
    *     PUBLIC METHODS
    ******************************************************/

    public getEncryptedClientDeviceFingerprint(applicationComponent: string, activeLegalEntityId: number): Promise<{encyptedFingerprint: string, encryptedActiveLegalEntityId: string}> {
        switch(applicationComponent) {
            case "buildingsMembership": {
                return SharedUtils.symmetricEncrypt(this.clientDeviceInfo().getClientDeviceFingerprint(), environment.buildingsMembershipClientAccessUuidToken, environment.buildingsMembershipEncryptSalt).then( (encryptedFingerprint: string)=> {
                    if(activeLegalEntityId && activeLegalEntityId > 0) {
                        return SharedUtils.symmetricEncrypt(activeLegalEntityId.toString(), environment.buildingsMembershipClientAccessUuidToken, environment.buildingsMembershipEncryptSalt).then( (encryptedActiveLegalEntityId: string) => {
                            return {encyptedFingerprint: encryptedFingerprint, encryptedActiveLegalEntityId: encryptedActiveLegalEntityId};
                        });
                    }
                    else {
                        return {encyptedFingerprint: encryptedFingerprint, encryptedActiveLegalEntityId: ""};
                    }
                });
            }
            case "buildingsDossier": {
                return SharedUtils.symmetricEncrypt(this.clientDeviceInfo().getClientDeviceFingerprint(), environment.buildingsDossierClientAccessUuidToken, environment.buildingsDossierEncryptSalt).then( (encryptedFingerprint: string)=> {
                    if(activeLegalEntityId && activeLegalEntityId > 0) {
                        return SharedUtils.symmetricEncrypt(activeLegalEntityId.toString(), environment.buildingsDossierClientAccessUuidToken, environment.buildingsDossierEncryptSalt).then( (encryptedActiveLegalEntityId: string) => {
                            return {encyptedFingerprint: encryptedFingerprint, encryptedActiveLegalEntityId: encryptedActiveLegalEntityId};
                        });
                    }
                    else {
                        return {encyptedFingerprint: encryptedFingerprint, encryptedActiveLegalEntityId: ""};
                    }
                });
            }
            default: {
                return Promise.resolve({encyptedFingerprint: "", encryptedActiveLegalEntityId: ""});
            }
        }
    }

    /*******************************************************
    *     PRIVATE METHODS
    ******************************************************/

    private getClientDeviceFingerprint(): string {
        return this.clientDeviceInfo().getClientDeviceFingerprint();
    }

    private logClientDeviceInfo(): void {
        console.log("BuildingsCookieService.logClientDeviceInfo() - DEVICE INFO ...");
        console.log("DEVICE: " + this.clientDeviceInfo().device);
        console.log("DEVICE TYPE: " + this.clientDeviceInfo().deviceType);
        console.log("OS: " + this.clientDeviceInfo().os);
        console.log("OS VERSION: " + this.clientDeviceInfo().os_version);
        console.log("BROWSER: " + this.clientDeviceInfo().browser);
        console.log("BROWSER VERSION: " + this.clientDeviceInfo().browser_version);
        console.log("ORIENTATION: " + this.clientDeviceInfo().orientation);
        console.log("USER AGENT: " + this.clientDeviceInfo().userAgent);
        console.log("IS DESKTOP? " + this.clientDeviceInfo().isDesktop());
        console.log("IS TABLET? " + this.clientDeviceInfo().isTablet());
        console.log("IS MOBILE? " + this.clientDeviceInfo().isMobile());
        console.log("LANGUAGES " + this.clientDeviceInfo().languages);
        console.log("PROCESSORS " + this.clientDeviceInfo().processors);
        console.log("TIME ZONE " + this.clientDeviceInfo().timeZone);
        console.log("CALENDAR " + this.clientDeviceInfo().calendar);

        console.log("CLIENT DEVICE FINGERPRINT " + this.clientDeviceInfo().getClientDeviceFingerprint);

        ClientDeviceInfo.logNavigatorProperties();
    }

    /****************************************
    public getClientUserCitizenInfo(): CitizenInfo | undefined {
        if( SharedUtils.inputValueIsPresent(this.clientUserInfo) && (SharedUtils.inputValueIsPresent(this.clientUserInfo?.citizenInfo)) ) {
            return this.clientUserInfo!.citizenInfo;
        }
        if( this.isInLocalStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.citizenInfo) return this.clientUserInfo.citizenInfo;
        }
        if( this.isInSessionStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.citizenInfo) return this.clientUserInfo.citizenInfo;
        }
        return undefined;
    }

    public getClientUserinfo(): UserInfo | undefined {
        if(SharedUtils.inputValueIsPresent(this.clientUserInfo) && (this.clientUserInfo instanceof UserInfo) && (this.clientUserInfo?.id ?? 0) > 0) {
            return this.clientUserInfo;
        }
        if( this.isInLocalStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo) return this.clientUserInfo;
            // this.clientUserInfo = Object.assign(new UserInfo(), userInfo);
        }
        if( this.isInSessionStorage("userInfo") ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem("userInfo") ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo) return this.clientUserInfo;
            // this.clientUserInfo = Object.assign(new UserInfo(), userInfo);
        }
        return undefined;
    }

    public getClientUserActiveLegalEntity(): LegalEntityInfo | undefined {
        if( SharedUtils.inputValueIsPresent(this.clientUserInfo) && SharedUtils.inputValueIsPresent(this.clientUserInfo?.activeLegalEntityInfo) ) {
            return this.clientUserInfo!.activeLegalEntityInfo;
        }
        if (this.isInSessionStorage('userInfo')) {
            let userInfo: UserInfo = JSON.parse(sessionStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.activeLegalEntityInfo) return this.clientUserInfo.activeLegalEntityInfo;
        }
        if (this.isInLocalStorage('userInfo')) {
            let userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.activeLegalEntityInfo) return this.clientUserInfo.activeLegalEntityInfo;
        }
        return undefined;
    }

    public getClientUserLegalEntitiesBasicInfo(): LegalEntityBasicInfo[] | undefined {
        if( SharedUtils.inputValueIsPresent(this.clientUserInfo) && SharedUtils.inputValueIsPresent(this.clientUserInfo?.legalEntitiesBasicInfo) ) {
            return this.clientUserInfo?.legalEntitiesBasicInfo;
        }
        if (this.isInSessionStorage('userInfo')) {
            let userInfo: UserInfo = JSON.parse(sessionStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.legalEntitiesBasicInfo) return this.clientUserInfo.legalEntitiesBasicInfo;
        }
        if (this.isInLocalStorage('userInfo')) {
            let userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo = UserInfo.parseUserInfo(userInfo);
            if (this.clientUserInfo?.legalEntitiesBasicInfo) return this.clientUserInfo.legalEntitiesBasicInfo;
        }
        return undefined;
    }
    *****************************************/
    
    public getClientUser(): UserInfo | undefined {
        if( this.instantiateClientUserInfo() ) {
            return this.clientUserInfo();
        } else {
            return undefined;
        }
    }

    public getClientUserCitizen(): CitizenInfo | undefined {
        if( this.instantiateClientUserInfo() && (SharedUtils.inputValueIsPresent(this.clientUserInfo()?.citizenInfo)) ) {
            return this.clientUserInfo()!.citizenInfo;
        } else {
            return undefined;
        }
    }

    public getClientUserActiveLegalEntity(): LegalEntityInfo | undefined {
        if(this.instantiateClientUserInfo() && (SharedUtils.inputValueIsPresent(this.clientUserInfo()?.activeLegalEntityInfo))) {
            return this.clientUserInfo()!.activeLegalEntityInfo;
        } else {
            return undefined;
        }
    }

    public getClientRelUserActiveLegalEntity(): UserLegalEntityInfo | undefined {
        if(this.instantiateClientUserInfo() && (SharedUtils.inputValueIsPresent(this.clientUserInfo()?.activeUserLegalEntityInfo))) {
            return this.clientUserInfo()!.activeUserLegalEntityInfo;
        } else {
            return undefined;
        }
    }

    public getClientUserLegalEntitiesBasicInfo(): LegalEntityBasicInfo[] | undefined {
        if(this.instantiateClientUserInfo() && (SharedUtils.inputValueIsPresent(this.clientUserInfo()?.legalEntitiesBasicInfo))) {
            return this.clientUserInfo()?.legalEntitiesBasicInfo;
        } else return undefined;
    }

    public getClientUserActiveLegalEntityBasicDetail(): UserLegalEntityBasicDetail {
        const userLegalEntityDetail: UserLegalEntityBasicDetail = new UserLegalEntityBasicDetail();
        userLegalEntityDetail.userId = this.clientUserInfo()?.id;
        userLegalEntityDetail.userRoles = this.clientUserInfo()?.roles;
        userLegalEntityDetail.legalEntityId = this.clientUserInfo()?.activeUserLegalEntityInfo?.legalEntityId;
        userLegalEntityDetail.legalEntityRole = this.clientUserInfo()?.activeUserLegalEntityInfo?.legalEntityRole;
        userLegalEntityDetail.userLegalEntityRoles = this.clientUserInfo()?.activeUserLegalEntityInfo?.userLegalEntityRoles;
        return userLegalEntityDetail;
    }
/*    
    public getClientUserId(): number | undefined {
        if(this.instantiateClientUserInfo()) {
            return this.clientUserInfo().id;
        } else return undefined;
    }

    public getClientUserInstagramNickname(): string |undefined {
        if ( this.isInSessionStorage('userInfo') ) {
            return JSON.parse(sessionStorage.getItem('userInfo') ?? '').instagramNickname;
        }
        if ( this.isInLocalStorage('userInfo') ) {
            return JSON.parse(localStorage.getItem('userInfo') ?? '').instagramNickname;
        }
        return undefined;
    }

    public getClientUserNickname(): string | undefined {
        if( this.isInLocalStorage("userInfo")) {
            return JSON.parse(localStorage.getItem("userInfo") ?? "").nickname;
        }
        if( this.isInSessionStorage("userInfo")) {
            return JSON.parse(sessionStorage.getItem("userInfo") ?? "").nickname;
        }
        return undefined;
    }

    public getClientUserEmail(): string | undefined {
        if ( SharedUtils.inputValueIsPresent(this.clientUserInfo()?.email) ) {
            return this.clientUserInfo()?.email;
        }
        if ( this.isInLocalStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().email;
        }
        if ( this.isInSessionStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().email;
        }
        return undefined;
    }

    public getClientUserDateStart(): Date | undefined {
        if ( SharedUtils.inputValueIsPresent(this.clientUserInfo()?.dateStart) ) {
            return this.clientUserInfo()?.dateStart;
        }
        if ( this.isInLocalStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().dateStart;
        }
        if ( this.isInSessionStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().dateStart;
        }
        return undefined;
    }

    public getClientUserStatusId(): number | undefined {
        if ( SharedUtils.inputValueIsPresent(this.clientUserInfo()?.statusId) ) {
            return this.clientUserInfo()?.statusId;
        }
        if ( this.isInLocalStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(localStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().statusId;
        }
        if ( this.isInSessionStorage('userInfo') ) {
            const userInfo: UserInfo = JSON.parse(sessionStorage.getItem('userInfo') ?? "");
            this.clientUserInfo.set(Object.assign(new UserInfo() , userInfo));
            return this.clientUserInfo().statusId;
        }
        return undefined;
    }
*/
    /*********************************************************
    **********************************************************
    *********************************************************/
}
